//noinspection JSUnusedGlobalSymbols

/**
 * --------------------------------------------------------------------------
 * Hive Component: Extensions/Utils.js
 * Copyright (c) 2005 - 2022 Paul Höcker / PHMG All rights reserved.
 * Licensed under GNU General Public License version 2 or later
 * --------------------------------------------------------------------------
 */

/**
 * @callback HTMLReplacementCallback
 * @param {HTMLElement} replacement
 * @returns {void}
 */

const NAME = 'Utils'
const VERSION = '1.1.10'

// Constants
const EVENT_KEYS = {
	DOM_CHANGE               : 'joomla:updated',
	FORM_VALIDATION_CHANGE   : 'form:validation:change',
	FORM_VALIDATION_DISABLED : 'form:validation:disabled',
	FORM_VALIDATION_ENABLED  : 'form:validation:enabled',
	FORM_BEFORE_VALIDATE     : 'form:before:validate',
	FORM_INIT_VALIDATOR      : 'form:validator:init',
	LOAD_INIT                : 'init.apis.load',
	LOAD_COMPLETE            : 'complete.apis.load',
	LOAD_ERROR               : 'error.apis.load',
}

/**
 *
 * @interface Utils
 */
const Utils = {
	Timers : {}, Modals : {}, Token : '',

	/** @type {function(method:string, element:HTMLElement, options:object)|null} */
	BSBridge : null,

	_Events : { 'windowLoad' : { 'callbacks' : [], 'fired' : false } },

	_DEBUG : null,

	NAME : NAME, VERSION : VERSION, EVENT_KEYS : EVENT_KEYS, BS_VERSION : 0,

	/**
	 * FRAMEWORKS
	 */

	/**
	 * Set the bs version number
	 * @param {number} version
	 */
	setBsVersion : version => {
		Utils.BS_VERSION = version;
	},

	/**
	 * Get the bs version number
	 * @returns {number}
	 */
	getBsVersion : () => Utils.BS_VERSION,

	/**
	 * Get the "data-" prefix
	 * @returns {string}
	 */
	getBsPrefix : () => Utils.BS_VERSION > 4 ? 'bs-' : '',

	/**
	 * Get the bs bridge
	 * @return {function(method:string, element:HTMLElement|string, options:object|null)|null}
	 */
	getBsBridge : () => {
		const self = Utils;
		let version = self.BS_VERSION;
		if (!version) {
			console.log('ERROR: BS Version undefined')
			return null;
		}

		if (self.BSBridge == null) {
			if (version === 5) {
				const bs = self.getBootstrap(true);
				self.BSBridge = (method, element, options) => {
					//noinspection JSUnusedAssignment
					options = options || {};
					switch (method) {
						case "collapse.show" :
							bs.Collapse.getOrCreateInstance(element, { toggle : false }).show();
							break;
						case "collapse.hide" :
							bs.Collapse.getOrCreateInstance(element, { toggle : false }).hide();
							break;
						case "modal.show" :
							bs.Modal.getOrCreateInstance(element).show();
							break;
						case "modal.hide" :
							bs.Modal.getOrCreateInstance(element).hide();
							break;
						case "tooltip" :
							bs.Tooltip.getOrCreateInstance(element);
							break;
						case "tooltip.enable" :
							bs.Tooltip.getOrCreateInstance(element).enable();
							break;
						case "tooltip.disable" :
							bs.Tooltip.getOrCreateInstance(element).disable();
							break;
					}
				}
			} else {
				const jq = self.getJQuery(true);
				self.BSBridge = (method, element, options) => {
					//noinspection JSUnusedAssignment
					options = options || {};
					switch (method) {
						case "collapse.show" :
							jq(element).collapse('show');
							break;
						case "collapse.hide" :
							jq(element).collapse('hide');
							break;
						case "modal.show" :
							jq(element).modal('show');
							break;
						case "modal.hide" :
							jq(element).modal('hide');
							break;
						case "tooltip" :
							jq(element).tooltip();
							break;
						case "tooltip.enable" :
							jq(element).tooltip('enable');
							break;
						case "tooltip.disable" :
							jq(element).tooltip('disable');
							break;
						//case "scrollspy" :
						//	// jq(element).scrollspy(options);
						//	break;
						//case "scrollspy.refresh" :
						//	// jq(element).scrollspy('refresh');
						//	break;

					}
				}
			}
		}

		return self.BSBridge;
	},

	/**
	 * Get the global Bootstrap interface if it exists
	 * @param {boolean} [required]
	 * @return {{Alert:*,Button:*,Collapse:*,Carousel:*,Dropdown:*,Modal:*,Offcanvas:*,Popover:*,Tooltip:*,Scrollspy:*,Tab:*,Toast:*}|null}
	 */
	getBootstrap : required => {
		if (required && !window.bootstrap) {
			throw new Error('Fatal Error: Bootstrap undefined');
		}

		return window.bootstrap;
	},
	/**
	 * Get the global jQuery interface if it exists
	 * @param {boolean} [required]
	 * @returns {jQuery|null}
	 */
	getJQuery : required => {
		if (required && !window.jQuery) {
			throw new Error('Fatal Error: jQuery undefined');
		}

		return window.jQuery;
	},
	/**
	 * Get the global Joomla interface if it exists
	 * @param {boolean} [required]
	 * @return {Joomla|null}
	 */
	getJoomla : required => {
		if (required && !window.Joomla) {
			throw new Error('Fatal Error: Joomla undefined');
		}

		return window.Joomla;
	},
	/**
	 * Get the global Joomla interface if it exists
	 * @param {boolean} [required]
	 * @return {APIS|null}
	 */
	getAPIS : required => {
		if (required && !window.apis) {
			throw new Error('Fatal Error: APIS undefined');
		}

		return window.apis;
	},

	/**
	 * ### COMPONENTS
	 */

	/**
	 * Get a modal instance
	 * @param {string} id
	 * @param {string|Node} [dialog]
	 * @return {Node}
	 */
	getModal : (id, dialog) => {
		const self = Utils;

		let dialogString = typeof dialog === 'string' ? dialog : '';
		if (!self.Modals[id]) {
			let Modal = document.querySelector('#modal-' + id)
			if (!Modal) {
				Modal = self.htmlToElement(`<div class="modal fade" id="modal-${id}" tabindex="-1" role="dialog"><div class="modal-dialog">${dialogString}</div></div>`);
				if (dialogString) dialog = null;
				document.body.appendChild(Modal);
			}
			self.Modals[id] = Modal;
		}

		if (dialog) {
			let dialogElement = dialogString ? self.htmlToElement(dialog) : dialog;
			self.Modals[id].querySelector('.modal-dialog').replaceChildren(dialogElement);
		}

		return self.Modals[id];
	},

	/**
	 * ### MESSAGING
	 */

	/**
	 * Display an alert
	 * @param {string} message
	 * @param {string} [type]
	 * @param {*} [data]
	 * @param {number|boolean} [dismiss] Time in s/ms until the message disappears
	 */
	displayMessage : (message, type, data, dismiss) => {
		type = type || 'info';
		data = data || null;

		if (!message || message === '' || message === 'error' || message === 'parsererror') {
			message = 'GLOBAL_ERROR_UNDEFINED';
			type = 'danger';
		}

		// Translate message
		const J = Utils.getJoomla();
		if (message && typeof message === 'string' && J) {
			//noinspection JSUnresolvedVariable
			message = J.Text._(message);
		}

		// Log
		if (data && typeof data !== 'undefined') {
			console.log(message, data);
		}

		// Get the alert id
		let alertId = 'alert-general';
		let tmp = type.split('.');
		if (tmp[1] !== undefined) {
			type = tmp[0];
			alertId = tmp[1] + '-alert';
		}

		// Sanitize the type
		switch (type) {
			case 'exception' :
			case 'error' :
				type = 'danger';
				break;
			case 'hint':
			case 'notice':
				type = 'info';
				break;
			case 'alert':
				type = 'warning';
				break;
		}

		// Create the alert and modal
		const alert = dismiss === false ?
			Utils.htmlToElement(`<div class="alert joomla-alert alert-${type} lead" id="${alertId}" role="alert"><div class="alert-heading"><span class="${type}"></span></div><div class="alert-wrapper"><div class="alert-message">${message}</div></div></div>`) :
			Utils.htmlToElement(`<div class="alert joomla-alert alert-${type} lead alert-dismissible" id="${alertId}" role="alert"><button type="button" class="joomla-alert--close close btn-close" aria-label="schließen"><span aria-hidden="true">&times;</span></button><div class="alert-heading"><span class="${type}"></span></div><div class="alert-wrapper"><div class="alert-message">${message}</div></div></div>`);
		const modal = Utils.getModal('alert', alert);

		// noinspection JSUnresolvedFunction
		Utils.getBsBridge()('modal.show', modal);

		if (typeof dismiss === "number" && dismiss > 0) {
			if (dismiss < 10) dismiss *= 1000;
			Utils.addTimer(() => {
				Utils.hideMessage();
			}, dismiss, 'alert-auto-dismiss');
		}

		return Utils;
	},

	/**
	 * Hide an alert
	 * @param {string} [type]
	 */
	hideMessage : (type) => {
		//type = type || '';

		const modal = Utils.getModal('alert');

		if (typeof type === 'string' && type.length) {
			// Get the alert id
			let alertId = 'alert-general';
			let tmp = type.split('.');

			if (tmp[1] !== undefined) {
				type = tmp[0];
				alertId = tmp[1] + '-alert';
			}
			if (!document.querySelector(`#${alertId}.alert-${type}`)) return Utils;
		}

		Utils.getBsBridge()('modal.hide', modal);

		return Utils;
	},

	/**
	 * Grab system message
	 * @param {string} [type]
	 * @param {*} [data]
	 * @param {number} [dismiss]
	 */
	grabMessage : (type, data, dismiss) => {
		let selector = 'joomla-alert[type' + (typeof type === 'string' ? `="${type}"` : '') + '] > .alert-wrapper'; // div.alert-wrapper
		const systemMessage = document.querySelector(selector);
		if (!systemMessage) return false;

		type = type || systemMessage.parentElement.getAttribute('type');
		Utils.displayMessage(systemMessage.innerHTML, type, data, dismiss);
		systemMessage.remove();

		return true;
	},

	/**
	 * Shortcut for an error message
	 * @param {string} message
	 * @param {*} [data]
	 * @param {number|boolean} [dismiss]
	 */
	error : (message, data, dismiss) => {
		return Utils.displayMessage(message, 'error', data, dismiss);
	},

	/**
	 * Shortcut for info alert
	 * @param {string} message
	 * @param {*} [data]
	 * @param {number|boolean} [dismiss]
	 */
	info : (message, data, dismiss) => {
		return Utils.displayMessage(message, 'info', data, dismiss);
	},

	/**
	 * Shortcut for success alert
	 * @param {string} message
	 * @param {*} [data]
	 * @param {number|boolean} [dismiss]
	 */
	success : (message, data, dismiss) => {
		return Utils.displayMessage(message, 'success', data, dismiss);
	},

	/**
	 * Shortcut for warning alert
	 * @param {string} message
	 * @param {*} [data]
	 * @param {number|boolean} [dismiss]
	 */
	warning : (message, data, dismiss) => {
		return Utils.displayMessage(message, 'warning', data, dismiss);
	},

	/**
	 * ## EVENT HANDLING
	 */

	/**
	 * Add window load listeners
	 * @param {function} callback
	 */
	addWindowLoadListener : callback => {
		if (Utils._Events["windowLoad"].fired) {
			callback(new CustomEvent("load"))
		} else {
			Utils._Events["windowLoad"].callbacks.push(callback)
		}
	},

	/**
	 * ## FORM
	 */

	/**
	 * Get the form token
	 * @param {string} [tokenId]
	 * @return {string}
	 */
	getToken : (tokenId) => {
		if (!Utils.Token.length) {
			tokenId = tokenId || 'form-token';
			let element = document.getElementById(tokenId);
			if (!element) {
				const elements = [].slice.call(document.getElementsByTagName('input'));
				elements.forEach(input => {
					if (input.type === 'hidden' && input.value === '1' && input.name.length === 32) {
						element = input;
					}
				});
			}

			Utils.Token = element ? element.getAttribute('name') : '';
		}

		return Utils.Token;
	},

	/**
	 * Set the form token
	 * @param {string} tokenValue
	 * @param {string} [tokenId]
	 */
	setToken : (tokenValue, tokenId) => {
		tokenId = tokenId || 'form-token';
		const element = document.getElementById(tokenId);
		if (!element) return;

		element.setAttribute('name', tokenValue);
	},

	/**
	 * Set required attribute
	 * @param {HTMLElement} field
	 * @param {boolean} require
	 */
	setRequired : (field, require) => {
		if (require) {
			field.required = true;
			field.classList.add('required')
			field.removeAttribute('data-required-suppressed')
		} else {
			field.required = false;
			field.classList.remove('required')
			field.setAttribute('data-required-suppressed', '')
		}
	},

	/**
	 * ## TIMERS / INTERVALS
	 */

	/**
	 * Add a timer
	 * @param {Function} fn
	 * @param {Number} ms
	 * @param {String} name
	 */
	addTimer : (fn, ms, name = '') => {
		const self = Utils;
		name = name === '' ? self.random() : name;
		self.clearTimer(name);
		self.Timers[name] = setTimeout(fn, ms);
	},

	/**
	 * Clear a previously defined timer
	 * @param {string} name
	 */
	clearTimer : name => {
		const self = Utils;
		if (self.Timers[name]) {
			clearTimeout(self.Timers[name]);
		}
	},

	/**
	 * ## OBJECTS / ARRAYS
	 */

	/**
	 * Merge to arrays uniquely
	 * @param {*[]} a
	 * @param {*[]} b
	 * @param predicate
	 * @returns {*[]}
	 */
	merge : (a, b, predicate = (a, b) => a === b) => {
		const c = [...a]; // copy to avoid side effects
		// add all items from B to copy C if they're not already present
		b.forEach((bItem) => (c.some((cItem) => predicate(bItem, cItem)) ? null : c.push(bItem)))
		return c;
	},

	/**
	 * Filter object properties
	 * @param {object|null} data
	 * @param {string[]|null} remove
	 *
	 * @return {{}}
	 */
	filter : (data = null, remove = null) => {
		let filtered = {};
		if (!data || typeof data !== 'object') {
			return filtered;
		}

		remove = remove || [];

		for (let key in data) {
			if (data.hasOwnProperty(key) && remove.indexOf(key) < 0 && data[key] !== null) {
				filtered[key] = data[key];
			}
		}

		return filtered;
	},

	/**
	 * Check if value is an object
	 * @param {*} value
	 * @returns {boolean}
	 */
	isObject : value => {
		return value // null and undefined
			&& value.constructor === Object // because Object.keys(new Date()).length === 0;
			&& !Array.isArray(value) // arrays
		/* && typeof value === 'object'*/
	},

	/**
	 * Check if value is an empty object
	 * @param {*} value
	 * @returns {boolean}
	 */
	isEmptyObject : value => {
		return Utils.isObject(value) // null and undefined check
			&& Object.keys(value).length === 0
		//&& Object.getPrototypeOf(obj) === Object.prototype;
	},

	/**
	 * ## DOM / HTML
	 */

	/**
	 *
	 * @param {HTMLElement|string} elementOrSelector
	 * @param {HTMLElement|Node|string} replacementOrString
	 * @param {HTMLReplacementCallback|null} [callback]
	 * @param {{}|bool} [options]
	 */
	replaceElement : (elementOrSelector, replacementOrString, callback = null, options = {}) => {
		const element = typeof elementOrSelector == "string" ? document.querySelector(elementOrSelector) : elementOrSelector;
		const replacement = typeof replacementOrString == "string" ? Utils.htmlToElement(replacementOrString) : replacementOrString;

		if (!element || !replacement) {
			Utils.error('GLOBAL_WARNING_HTML_INVALID', { element, replacement });
			return;
		}

		if (options === false) {
			options = { transition : false };
		}

		options = Object.assign({ transition : true, duration : 600 }, options);

		const parentNode = element.parentNode;
		parentNode.replaceChild(replacement, element);

		if (!options.transition) {
			replacement.classList.remove('fade');
			replacement.classList.add('show');

			//element.classList.remove('fade');
			//parentNode.replaceChild(replacement, element);
			if (typeof callback === 'function') {
				callback(replacement);
			}
			parentNode.dispatchEvent(new CustomEvent(EVENT_KEYS.DOM_CHANGE, { bubbles : true, cancelable : true }));

			return;
		}

		replacement.classList.add('fade', 'show');
		//if (typeof callback === 'function') {
		//	callback(replacement);
		//}
		//parentNode.dispatchEvent(new CustomEvent(EVENT_KEYS.DOM_CHANGE, { bubbles : false }));
		//element.classList.remove('show');
		//element.classList.add('fade');

		Utils.addTimer(() => {
			// parentNode.replaceChild(replacement, element);
			//replacement.classList.add('show');
			if (typeof callback === 'function') {
				callback(replacement);
			}
			parentNode.dispatchEvent(new CustomEvent(EVENT_KEYS.DOM_CHANGE, { bubbles : true, cancelable : true }));
		}, options.duration);
	},

	/**
	 * Replace a form content
	 * @param {string} formName
	 * @param {string} replacementString
	 */
	replaceDataForm : (formName, replacementString) => {
		const formElement = document.querySelector('[data-content="' + formName + '"]');
		Utils.replaceElement(formElement, replacementString, element => {
			if (document.formvalidator) {
				document.formvalidator.attachToForm(element.closest('form'));
			} else {
				const jq = Utils.getJQuery(false);
				if (jq && typeof jq.fn.validate === 'function') {
					const validator = jq(element).closest('form').validate();
					if (validator) {
						//noinspection JSUnresolvedFunction
						validator.resetForm();
					}
				}
			}

		})
	},

	/**
	 * @param {HTMLElement|Node} newNode
	 * @param {HTMLElement|Node} referenceNode
	 */
	insertAfter : (newNode, referenceNode) => {
		referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
		referenceNode.parentNode.dispatchEvent(new CustomEvent(EVENT_KEYS.DOM_CHANGE, { bubbles : true, cancelable : true }));
	},

	/**
	 * @param {HTMLElement|Node} newNode
	 * @param {HTMLElement|Node} referenceNode
	 */
	appendTo : (newNode, referenceNode) => {
		referenceNode.appendChild(newNode);
		referenceNode.dispatchEvent(new CustomEvent(EVENT_KEYS.DOM_CHANGE, { bubbles : true, cancelable : true }));
	},

	/**
	 * @param {HTMLElement|Node} newNode
	 * @param {HTMLElement|Node} oldNode
	 */
	replace : (newNode, oldNode) => {
		let parentNode = oldNode.parentNode;
		parentNode.replaceChild(newNode, oldNode);
		parentNode.dispatchEvent(new CustomEvent(EVENT_KEYS.DOM_CHANGE, { bubbles : true, cancelable : true }));
	},

	/**
	 * Create wrapper with document.createElement()
	 * @param {HTMLElement|Node} targetNode
	 * @param {HTMLElement|Node} wrapperNode
	 */
	wrap : (targetNode, wrapperNode) => {
		let parentNode = targetNode.parentNode;
		parentNode.insertBefore(wrapperNode, targetNode);
		wrapperNode.appendChild(targetNode);
	},

	/**
	 * Create a html node from string
	 * @param {string} htmlString
	 * @return {Node}
	 */
	htmlToElement : htmlString => {
		let template = /** @type {HTMLTemplateElement} */ document.createElement('template');
		template.innerHTML = htmlString.trim();
		return template.content.firstChild;
	},

	/**
	 * ## STRINGS / FORMATTING
	 */

	/**
	 *
	 * @param {number} length
	 * @returns {string}
	 */
	random : (length = 8) => {
		// Declare all characters
		let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

		// Pick characters randomly
		let str = '';
		for (let i = 0; i < length; i++) {
			str += chars.charAt(Math.floor(Math.random() * chars.length));
		}

		return str;
	},

	/**
	 * Round number to two digits
	 * @param {number} num
	 * @return {number}
	 */
	roundNumber : num => {
		return Number((Math.round(num * 100) / 100).toFixed(2));
	},

	/**
	 * Calculate the progress
	 * @param {number} position
	 * @param {number} duration
	 * @return {number}
	 */
	percentage : (position, duration) => {
		let progress = !position || !duration ? 0 : Utils.roundNumber((position / duration) * 100);
		return Math.min(progress, 100);
	},

	/**
	 * Get a websafe string
	 * @param {string|*} input
	 * @param {object} [options]
	 * @returns {string}
	 */
	makeWebsafe : (input, options) => {
		const preReplacements = {
			// Combining Diaeresis
			"\u0308" : "e",
			//"U\u0308" : "Ue", "u\u0308" : "ue",
			//"A\u0308" : "Ae", "a\u0308" : "ae",
			//"O\u0308" : "Oe", "o\u0308" : "oe",
		}
		const mainReplacements = {
			// Umlaute
			"\u00dc" : "Ue", "\u00c4" : "Ae", "\u00d6" : "Oe",
			"\u00fc" : "ue", "\u00e4" : "ae", "\u00f6" : "oe",
			// @ &
			"\u0040" : "_at_", "\u0026" : "_and_",
			// ß
			"\u00df" : "ss",
		}
		const stripRegex = /[^a-z\d\-_.]/gi;

		// Make sure value is a string
		let value;
		if (input === null || typeof input === "undefined" || typeof input === "symbol") {
			return '';
		}

		if (Array.isArray(input)) {
			value = input.join("-");
		} else if (typeof input !== "string") {
			value = input + '';
		} else {
			value = input;
		}
		value = value.trim();
		if (!value.length) return "";

		options = Object.assign({ space_to_underscore : true, keep_dots : true, to_lower_case : false }, options || {});
		// Set lower case
		if (options.to_lower_case) value = value.toLowerCase();
		// Replace spaces
		value = options.space_to_underscore ? value.replace(/\s+/g, '_') : value.replace(/\s+/g, '-')
		// Remove dots
		if (!options.keep_dots) value = value.replace(/[.]/g, '_');

		return value
			.replace(new RegExp(Object.keys(preReplacements).join('|'), 'gu'), (a) => preReplacements[a]) // Replace cd
			.replace(new RegExp('(' + Object.keys(mainReplacements).join('|') + ')', 'gu'), (a) => mainReplacements[a]) // Replace umlaute
			.replace(stripRegex, '') // Strip everything else
			;
	},

	/**
	 * MIXED
	 */

	/**
	 * Get NOW formatted
	 * @return {string}
	 */
	getISODate : () => {
		return new Date().toISOString();
	},

	/**
	 * ## COOKIES
	 */

	/**
	 * Get a cookie value
	 * @param {string} name
	 * @param {*} [def]
	 * @returns {string|*}
	 */
	getCookie : (name, def = null) => {
		let matches = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, '\\$1') + "=([^;]*)"));
		return matches && matches[1] !== undefined ? decodeURIComponent(matches[1]) : def;
	},

	/**
	 * ## DEBUG
	 */

	/**
	 * Check debug state
	 * @returns {boolean}
	 */
	idb : () => {
		const self = Utils;
		if (self._DEBUG === null) {
			self._DEBUG = self.getCookie('isDebug', 0);
		}

		return self._DEBUG;
	},

	/**
	 * Log on debug
	 * Returns Utils for chaining
	 * @param {*} data
	 * @param {{}|function|null|string} [caller]
	 *
	 * @return this
	 */
	odb : function(data, caller) {
		if (!Utils.idb()) return Utils;

		//console.log(arguments.length)
		// Handle more than two arguments
		//const args = [...arguments];
		//if (args.length > 1) caller = args.pop();
		//
		//for (let i = 0; i < args.length; i++) {
		//	console.log(i, args[i])
		//}

		let callerName;
		if (caller) {
			callerName = caller['dbName'] || null;
			if (!callerName) {
				try {
					callerName = (new Error()).stack.split("\n")[2].trim().split(" ")[1];
					caller['dbName'] = callerName;
				} catch (error) {}
			}
		}

		callerName = callerName ? callerName : 'Utils';
		console.log(callerName, data);

		return Utils;
	}
}

window.addEventListener("load", event => {
	Utils._Events["windowLoad"].callbacks.forEach(callback => callback(event))
	Utils._Events["windowLoad"].fired = true;
})

export default Utils;